import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  ColumnDef,
  ColumnFiltersState,
  HeaderGroup,
  Row,
  RowModel,
  Table,
  VisibilityState,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { URLS } from 'components/shared/App/constants';
import { selectors } from 'store';

import { FormattedCompany, formatCompanyData } from './helpers';

export interface CompanySearchTableHook {
  showTable: boolean;
  table: Table<FormattedCompany>;
  header: HeaderGroup<FormattedCompany>[];
  rows: RowModel<FormattedCompany>;
  onClick: (row: any) => void;
}

export default function useCompanySearchTable(): CompanySearchTableHook {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    cid: false,
  });

  const { push } = useHistory();

  const searchData = useSelector(selectors.infoSearch.getEntity);

  const onClick = useCallback(
    (row: Row<any>) => {
      const { original } = row;
      const cid = original?.cid;
      if (!cid) return;
      push(URLS.company.replace(':id', cid));
    },
    [push],
  );

  const data = useMemo(
    () => formatCompanyData(searchData?.hits?.company),
    [searchData],
  );

  const columns = useMemo<ColumnDef<FormattedCompany, any>[]>(
    () => [
      {
        accessorKey: 'cid',
        header: () => <p>CID</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'companyName',
        header: () => <p>Company Name</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'contactEmail',
        header: () => <p>Company Email</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'contactName',
        header: () => <p>Company Contact Name</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'contactPhone',
        header: () => <p>Company Phone</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'contactAddress',
        header: () => <p>Company Address</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'createdAt',
        header: () => <p>Company Created</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'updatedAt',
        header: () => <p>Company Updated</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
    ],
    [],
  );

  const table = useReactTable<FormattedCompany>({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      columnVisibility,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  return {
    showTable: data.length > 0,
    table,
    header: table.getHeaderGroups(),
    rows: table.getRowModel(),
    onClick,
  };
}
